import React, { useEffect, useState } from "react";

import {
  Box,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Pin, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { CustomText } from "@root/components/common/components";
import useChatHistory from "@root/hooks/useChatHistory";
import { PopoverDropdown } from "@root/components/common/dropdown";
import { useParams, useNavigate } from "react-router-dom";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useProjectsContext } from "@root/context/projects-context";
import { getChatRoute } from "@root/utils/common";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "@root/context/global-context";
import { ModalDelete } from "@root/components/modal-delete";
import { CommonButton } from "@root/components/common/button/commonButton";

interface Chat {
  isPinned: boolean;
  id: number;
  name: string;
  userId: number;
  mode: string;
  createdAt: string;
  updatedAt: string;
  isSelected: boolean;
  type: number;
  chat_title: string;
}

interface ThreadGroup {
  date: string;
  chats: Chat[];
}

interface ChatHistorySidebarProps {
  threads?: ThreadGroup[];
  onChatClick?: any;
  onChatDelete?: any;
  onChatRename?: (chat: Chat, newName: string) => void;
  onRefetchThreads?: () => void;
  loading?: boolean;
  isVisible?: boolean;
}

export const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({
  threads = [],
  onChatClick,
  onChatDelete,
  onChatRename,
  onRefetchThreads,
  loading = false,
  isVisible = false,
}) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { id: currentThreadId } = useParams();
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({});
  const [hoveredChatId, setHoveredChatId] = useState<number | null>(null);
  const [openMenuChatId, setOpenMenuChatId] = useState<number | null>(null);
  const { resetData } = useMainChatContext();
  const { setDocumentData } = useProjectsContext();
  const {
    toggleThreadPin: toggleThreadPinApi,
    deleteThread: deleteThreadApi,
    renameThread: renameThreadApi,
    threadActionLoading,
  } = useGlobalContext();
  const {
    isOpen: isRenameModalOpen,
    onOpen: openRenameModal,
    onClose: closeRenameModal,
  } = useDisclosure();
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDisclosure();
  const [selectedRenameChat, setSelectedRenameChat] = useState<Chat | null>(
    null
  );
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState("");
  const [selectedDeleteChat, setSelectedDeleteChat] = useState<Chat | null>(
    null
  );

  console.log("threads sidebar", threads);

  const resetRenameState = () => {
    setSelectedRenameChat(null);
    setRenameValue("");
    setRenameError("");
    closeRenameModal();
  };

  const resetDeleteState = () => {
    setSelectedDeleteChat(null);
    closeDeleteDialog();
  };

  const isActionLoading = (
    chatId: number | null | undefined,
    action: "pin" | "rename" | "delete"
  ) => {
    if (!chatId) return false;
    return Boolean(threadActionLoading?.[chatId]?.[action]);
  };

  const [hasBeenUncollapsed, setHasBeenUncollapsed] = useState(false);

  // Track when component becomes visible for the first time
  useEffect(() => {
    if (isVisible && !hasBeenUncollapsed && threads?.length > 0) {
      setHasBeenUncollapsed(true);
    }
  }, [isVisible, hasBeenUncollapsed, threads?.length]);

  useEffect(() => {
    setCollapsedSections((prev) => {
      const updated = { ...prev };
      threads?.forEach((group, index) => {
        if (updated[group?.date] === undefined) {
          // By default, all dates are collapsed (true)
          // Only the first (most recent) date should be uncollapsed (false) when first opened
          updated[group?.date] = hasBeenUncollapsed ? true : index !== 0;
        }
      });
      return updated;
    });
  }, [threads, hasBeenUncollapsed]);

  const toggleSection = (date: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const handleChatClick = (chat: Chat) => {
    // if (onChatClick) {
    //   onChatClick(chat);
    //   return;
    // }
    resetData();
    setDocumentData(null);
    navigate(getChatRoute(chat));
  };

  const handleChatDelete = (chat: Chat) => {
    setSelectedDeleteChat(chat);
    setOpenMenuChatId(null);
    openDeleteDialog();
  };

  const handleChatRename = (chat: Chat) => {
    setSelectedRenameChat(chat);
    setRenameValue(chat?.name || "");
    setRenameError("");
    setOpenMenuChatId(null);
    openRenameModal();
  };

  const handleChatPin = (chat: Chat) => {
    toggleThreadPinApi(chat?.id, {
      onSuccess: () => {
        onRefetchThreads?.();
      },
    });
    setOpenMenuChatId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDeleteChat) return;
    await deleteThreadApi(selectedDeleteChat?.id, {
      onSuccess: () => {
        onRefetchThreads?.();
        onChatDelete?.(selectedDeleteChat);
        resetDeleteState();
      },
    });
  };

  const handleRenameSubmit = async () => {
    if (!selectedRenameChat) return;
    const trimmedName = renameValue?.trim();
    if (!trimmedName) {
      setRenameError(
        t("validation.required", {
          defaultValue: "Name is required",
        })
      );
      return;
    }
    await renameThreadApi(selectedRenameChat?.id, trimmedName, {
      onSuccess: () => {
        onChatRename?.(selectedRenameChat, trimmedName);
        onRefetchThreads?.();
        resetRenameState();
      },
    });
  };

  const ChatItem: React.FC<{ chat: Chat }> = ({ chat }) => {
    const isActive = currentThreadId === chat?.id?.toString();
    const isPinLoading = isActionLoading(chat?.id, "pin");
    const isRenameLoading = isActionLoading(chat?.id, "rename");
    const isDeleteLoading = isActionLoading(chat?.id, "delete");

    return (
      <Flex
        position="relative"
        alignItems="center"
        justifyContent="space-between"
        px="4px"
        py="8px"
        borderRadius="6px"
        cursor="pointer"
        bg={isActive ? "#EBEBEB" : "transparent"}
        _hover={{ bg: isActive ? "#EBEBEB" : "#EBEBEB" }}
        onMouseEnter={() => setHoveredChatId(chat?.id)}
        onMouseLeave={() => setHoveredChatId(null)}
        onClick={() => handleChatClick(chat)}
      >
        <Flex direction="column" gap="2px" flex="1">
          <Flex align="center" gap="6px">
            {/* <Text
              fontSize="16px"
              fontWeight={600}
              color="black"
              noOfLines={1}
              textOverflow="ellipsis"
            >
              {chat?.chat_title}
            </Text> */}

            {chat?.isPinned && (
              <Box
                minW="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="#5C6670"
              >
                <Pin size={14} style={{ transform: "rotate(50deg)" }} />
              </Box>
            )}
          </Flex>

          <CustomText
            fontSize="13px"
            fontWeight={400}
            textColor="#5C6670"
            text={chat?.chat_title ?? chat?.name}
            noOfLines={2}
            textOverflow="ellipsis"
            width="100%"
            lineHeight="1.4"
          />
        </Flex>

        {(hoveredChatId === chat?.id ||
          isActive ||
          openMenuChatId === chat?.id) && (
            <PopoverDropdown
              placement="right-start"
              isOpen={openMenuChatId === chat?.id}
              onOpen={() => setOpenMenuChatId(chat?.id)}
              onClose={() => setOpenMenuChatId(null)}
              items={[
                {
                  id: "pin",
                  startIcon: (
                    <Pin size={14} style={{ transform: "rotate(50deg)" }} />
                  ),
                  text: t("translate.pin"),
                  onClick: () => handleChatPin(chat),
                  disabled: isPinLoading,
                  endIcon: isPinLoading ? <Spinner size="xs" /> : undefined,
                },
                {
                  id: "rename",
                  startIcon: <Pencil size={14} />,
                  text: t("translate.rename"),
                  onClick: () => handleChatRename(chat),
                  disabled: isRenameLoading,
                  endIcon: isRenameLoading ? <Spinner size="xs" /> : undefined,
                },
                {
                  id: "delete",
                  startIcon: <Trash2 size={14} />,
                  text: t("translate.delete"),
                  onClick: () => handleChatDelete(chat),
                  disabled: isDeleteLoading,
                  endIcon: isDeleteLoading ? <Spinner size="xs" /> : undefined,
                },
              ]}
            >
              <Box
                cursor="pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuChatId((prev) =>
                    prev === chat?.id ? null : chat?.id
                  );
                }}
              >
                <IconButton
                  size="xs"
                  minW="24px"
                  h="24px"
                  aria-label="Chat actions"
                  icon={<MoreVertical size={16} color="#5C6670" />}
                  background="transparent"
                  _hover={{ background: "transparent" }}
                  _active={{ background: "transparent" }}
                />
              </Box>
            </PopoverDropdown>
          )}
      </Flex>
    );
  };

  const DateSection: React.FC<{ threadGroup: ThreadGroup }> = ({
    threadGroup,
  }) => {
    const isCollapsed = collapsedSections[threadGroup?.date];

    return (
      <Box key={threadGroup?.date}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          px="0"
          py="6px"
          cursor="pointer"
          onClick={() => toggleSection(threadGroup?.date)}
        >
          <Text
            fontSize="12px"
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing="0.5px"
            color="#757575"
          >
            {threadGroup?.date}
          </Text>
          {isCollapsed ? (
            <ChevronDownIcon boxSize={4} color="#757575" />
          ) : (
            <ChevronUpIcon boxSize={4} color="#757575" />
          )}
        </Flex>

        {!isCollapsed && (
          <VStack spacing={1} align="stretch">
            {threadGroup?.chats?.map((chat: any) => (
              <ChatItem key={chat?.id} chat={chat} />
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  let sidebarContent: React.ReactNode = null;

  if (loading) {
    sidebarContent = (
      <Box
        p="16px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="100px"
      >
        <Spinner size="md" color="#757575" thickness="3px" />
      </Box>
    );
  } else if (!threads || threads?.length === 0) {
    sidebarContent = (
      <Box p="16px" textAlign="center">
        <Text fontSize="14px" color="#999999">
          No chat history available
        </Text>
      </Box>
    );
  } else {
    sidebarContent = (
      <Box
        width="100%"
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#e2e8f0",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#cbd5e0",
          },
        }}
      >
        <VStack spacing={2} align="stretch" p="8px 0">
          {threads?.map((threadGroup: any) => (
            <DateSection key={threadGroup?.date} threadGroup={threadGroup} />
          ))}
        </VStack>
      </Box>
    );
  }

  return (
    <>
      {sidebarContent}

      <Modal isOpen={isRenameModalOpen} onClose={resetRenameState} isCentered>
        <ModalOverlay />
        <ModalContent bg="#FFFFFF">
          <ModalHeader>
            {t("chatHistory.renameDialogTitle", {
              defaultValue: "Rename conversation",
            })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="#6B7280" mb={3}>
              {t("chatHistory.renameDialogSubtitle", {
                defaultValue:
                  "Give this conversation a friendly name to find it faster later.",
              })}
            </Text>
            <Input
              value={renameValue}
              onChange={(e) => {
                setRenameValue(e.target.value);
                if (renameError) {
                  setRenameError("");
                }
              }}
              placeholder={t("chatHistory.renamePlaceholder", {
                defaultValue: "Enter a new name",
              })}
              maxLength={80}
              autoFocus
            />
            {renameError && (
              <Text color="red.500" fontSize="xs" mt={2}>
                {renameError}
              </Text>
            )}
          </ModalBody>
          <ModalFooter gap="12px">
            <CommonButton
              variant="secondary"
              onClick={resetRenameState}
              height="40px"
            >
              {t("translate.cancel", { defaultValue: "Cancel" })}
            </CommonButton>
            <CommonButton
              variant="primary"
              onClick={handleRenameSubmit}
              isDisabled={!renameValue?.trim()}
              isLoading={isActionLoading(selectedRenameChat?.id, "rename")}
              height="40px"
            >
              {t("translate.save", { defaultValue: "Save" })}
            </CommonButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ModalDelete
        isOpen={isDeleteDialogOpen}
        onClose={resetDeleteState}
        onConfirm={handleDeleteConfirm}
        title={t("chat.deleteChat")}
        description={t("chat.areYouSure")}
        deleteButtonTitle={t("chat.deleteBtn", { defaultValue: "Delete" })}
        cancelButtonTitle={t("chat.cancelBtn", { defaultValue: "Cancel" })}
        isLoading={isActionLoading(selectedDeleteChat?.id, "delete")}
      />
    </>
  );
};

// Usage example in your sidebar:
export const SidebarWithChatHistory: React.FC = () => {
  const { threads, handleChatClick, refreshThreads } = useChatHistory();
  const { t } = useTranslation();

  return (
    <Box>
      {/* Your existing sidebar header and other components */}

      <Box>
        <Text
          fontSize="12px"
          fontWeight="600"
          textTransform="uppercase"
          letterSpacing="0.5px"
          color="#757575"
          mb="8px"
          px="0"
        >
          {t("sidebar.chatConversation")}
        </Text>

        <ChatHistorySidebar
          threads={threads || []}
          onChatClick={handleChatClick}
          onRefetchThreads={refreshThreads}
        // maxHeight="300px"
        />
      </Box>
    </Box>
  );
};
